import { useState, useRef } from "react";
import { Loader2, Clock, LogIn, LogOut } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { v4 as uuidv4 } from "uuid";

import {
  useCheckInMutation,
  useCheckOutMutation,
  useGetRecordsByRoleQuery,
  useGetMissedCheckoutAlertQuery,
} from "../../redux/hrms/attendanceAPI";

import ConfirmActionModal from "./ConfirmActionModal";
import { toLocalDateString } from "../../utils/dateFormat";

const AttendanceButton = ({ userID }) => {
  const { toast } = useToast();

  /* ---------------- SAFE GUARD ---------------- */
  const validUserID = userID != null && userID !== "undefined";
  const shouldSkip = !validUserID;

  /* ---------------- TODAY'S ATTENDANCE ---------------- */
  const today = toLocalDateString();

  const {
    data: todayAttendanceData,
    refetch: refetchTodayAttendance,
  } = useGetRecordsByRoleQuery(
    {
      page: 0,
      size: 1,
      sortBy: "checkInTime",
      sortDir: "desc",
      filter: {
        employeeName: "",
        fromDate: today,
        toDate: today,
        status: "",
        scope: "SELF",
      },
    },
    { skip: shouldSkip, refetchOnFocus: true }
  );

  /*
   * Today's attendance comes from a date-filtered query (latest
   * session first), not from a "recent records" list.
   */
  const todayAttendance = todayAttendanceData?.content?.[0] ?? null;

  /* ---------------- MISSED CHECKOUT ---------------- */
  const { data: missedCheckout } = useGetMissedCheckoutAlertQuery(
    shouldSkip ? undefined : userID,
    { skip: shouldSkip }
  );

  /* ---------------- MUTATIONS ---------------- */
  const [checkIn, { isLoading: isCheckingIn }] = useCheckInMutation();
  const [checkOut, { isLoading: isCheckingOut }] = useCheckOutMutation();

  /* ---------------- PREVENT DUPLICATE REQUESTS ---------------- */
  const isRequestInProgress = useRef(false);

  /* ---------------- LOADING ---------------- */
  const isLoading = isCheckingIn || isCheckingOut;

  /* ---------------- ATTENDANCE STATE ---------------- */
  const hasMissedCheckout =
    missedCheckout?.hasMissedCheckout === true;

  /*
   * Optimistic record temporarily overrides the real today's
   * attendance while the mutation/refetch is completing.
   */
  const [optimisticRecord, setOptimisticRecord] = useState(null);

  /*
   * Determine whether today's attendance session is still open.
   *
   * Priority:
   * 1. Optimistic state, when available.
   * 2. Real today's backend record.
   *
   * This no longer depends on the latest-10 history query.
   */
  const hasOpenRecord = optimisticRecord
    ? !optimisticRecord.checkOutTime
    : Boolean(
        todayAttendance?.checkInTime &&
        !todayAttendance?.checkOutTime
      );

  const [modalOpen, setModalOpen] = useState(false);

  /* ---------------- LOCATION ---------------- */
  const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(
          new Error("Geolocation is not supported in this browser")
        );
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  /* ---------------- REFRESH ATTENDANCE DATA ---------------- */
  const refreshAttendanceData = async () => {
    /*
     * The mutations already invalidate the "Attendance" tag; this
     * just waits for today's record so the optimistic state can be
     * dropped. A failed refetch must not turn a successful
     * check-in/out into an error toast.
     */
    try {
      await refetchTodayAttendance();
    } catch {
      /* ignore: tag invalidation / next focus will resync */
    }
  };

  /* ---------------- HANDLE CLICK ---------------- */
  const handleClick = async () => {
    // Prevent multiple rapid clicks
    if (isRequestInProgress.current) {
      toast({
        title: "Please wait",
        description: "Your previous request is being processed.",
      });
      return;
    }

    // Previous missed checkout blocks new check-in
    if (hasMissedCheckout) {
      toast({
        title: "Missed Checkout",
        description:
          "Please complete your previous checkout from the Dashboard popup before checking in.",
        variant: "destructive",
      });
      return;
    }

    // Today's record is open, so proceed to checkout
    if (hasOpenRecord) {
      openModal();
      return;
    }

    // Proceed with check-in
    isRequestInProgress.current = true;

    try {
      const coords = await getCurrentLocation();

      const idempotencyKey =
        `${userID}-checkin-${Date.now()}-${uuidv4()}`;

      await checkIn({
        userID,
        checkInLatitude: coords.latitude,
        checkInLongitude: coords.longitude,
        comments: null,
        checkInLocationName: "",
        checkInTime: null,
        idempotencyKey,
      }).unwrap();

      toast({
        variant: "success",
        title: "Checked in",
        description:
          "Your check-in was recorded successfully.",
      });

      /*
       * Immediately show CHECK OUT.
       *
       * The timestamp here is only temporary. It will be replaced
       * by the actual backend timestamp after refetch.
       */
      setOptimisticRecord({
        checkInTime: new Date().toISOString(),
        checkOutTime: null,
      });

      // Keep the optimistic state until today's record has refreshed
      await refreshAttendanceData();
    } catch (error) {
      handleAttendanceError(error);
    } finally {
      setOptimisticRecord(null);
      isRequestInProgress.current = false;
    }
  };

  /* ---------------- CHECK OUT ---------------- */
  const handleCheckOut = async () => {
    // Prevent multiple checkout attempts
    if (isRequestInProgress.current) {
      return;
    }

    closeModal();
    isRequestInProgress.current = true;

    try {
      const coords = await getCurrentLocation();

      const idempotencyKey =
        `${userID}-checkout-${Date.now()}-${uuidv4()}`;

      await checkOut({
        userID,
        checkOutLatitude: coords.latitude,
        checkOutLongitude: coords.longitude,
        comments: null,
        checkOutLocationName: "",
        checkOutTime: null,
        idempotencyKey,
      }).unwrap();

      toast({
        variant: "success",
        title: "Checked out",
        description:
          "Your check-out was recorded successfully.",
      });

      /*
       * Immediately show CHECK IN.
       *
       * The actual backend checkout timestamp will replace this
       * optimistic value after the today's query is refreshed.
       */
      setOptimisticRecord({
        checkInTime:
          todayAttendance?.checkInTime ??
          optimisticRecord?.checkInTime ??
          null,
        checkOutTime: new Date().toISOString(),
      });

      // Keep the optimistic state until today's record has refreshed
      await refreshAttendanceData();
    } catch (error) {
      handleAttendanceError(error);
    } finally {
      setOptimisticRecord(null);
      isRequestInProgress.current = false;
    }
  };

  /* ---------------- ERROR HANDLING ---------------- */
  const handleAttendanceError = (error) => {
    let message =
      error?.data?.message ||
      error?.message ||
      "Something went wrong.";

    if (error?.data?.error === "DUPLICATE_REQUEST") {
      message =
        "This request has already been processed. Please refresh.";
    } else if (
      error?.data?.error === "ACTIVE_CHECKIN_EXISTS"
    ) {
      message =
        "You already have an active check-in for today.";
    } else if (
      message.includes("MISSED_CHECKOUT_PENDING")
    ) {
      message =
        "Please complete your previous checkout from the Dashboard popup.";
    } else if (
      message.includes("No active session found")
    ) {
      message =
        "No active session found for checkout.";
    }

    toast({
      title: "Attendance update failed",
      description: message,
      variant: "destructive",
    });
  };

  /* ---------------- LOADING STATE ---------------- */
  if (shouldSkip) {
    return null;
  }

  /* ---------------- DISPLAY HELPERS ---------------- */
  const formatCheckTime = (time) => {
    if (!time) return "";

    return new Date(time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /*
   * The UI uses optimistic state first, then today's real
   * backend attendance.
   */
  const effectiveToday =
    optimisticRecord ?? todayAttendance;

  const todayCheckInTime =
    effectiveToday?.checkInTime;

  const todayCheckOutTime =
    effectiveToday?.checkOutTime;

  /* ---------------- UI ---------------- */
  return (
    <>
      <div className="flex items-center gap-4 p-1.5">

        {/* Today's check-in time / missed checkout */}
        {hasMissedCheckout ? (
          <div className="flex items-center rounded-full bg-red-100 border border-red-300 px-2 sm:px-3 py-1.5 sm:py-2 mr-2 sm:mr-3 whitespace-nowrap">
            <span className="text-sm font-semibold text-red-700">
              <Clock className="h-3 w-3 inline mr-1" />
              Missed Checkout
            </span>
          </div>
        ) : (
          !todayCheckOutTime && (
            <div className="flex items-center rounded-full bg-muted border border-border px-2 sm:px-3 py-1.5 sm:py-2 mr-2 sm:mr-3 whitespace-nowrap">
              {todayCheckInTime ? (
                <span className="text-sm font-semibold">
                  <span className="text-green-600">
                    IN
                  </span>
                  :{" "}
                  <span className="text-foreground">
                    {formatCheckTime(todayCheckInTime)}
                  </span>
                </span>
              ) : (
                <span className="text-sm font-semibold text-muted-foreground">
                  No Check-In
                </span>
              )}
            </div>
          )
        )}

        {/* Attendance action button */}
        <button
          onClick={handleClick}
          disabled={
            isLoading ||
            isRequestInProgress.current ||
            hasMissedCheckout
          }
          className={[
            "h-8 w-8 rounded-full",
            "flex items-center justify-center",
            "transition-all duration-200",
            "shadow-md hover:shadow-lg",
            "hover:scale-110 active:scale-95 cursor-pointer",
            "disabled:opacity-60 disabled:cursor-not-allowed",

            hasMissedCheckout
              ? "bg-gray-400 text-white"
              : hasOpenRecord
                ? "bg-orange-500 hover:bg-orange-600 text-white"
                : "bg-green-600 hover:bg-green-700 text-white",
          ].join(" ")}
          title={
            hasMissedCheckout
              ? "Complete missed checkout first"
              : hasOpenRecord
                ? "Check Out"
                : "Check In"
          }
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : hasMissedCheckout ? (
            <Clock className="h-5 w-5" />
          ) : hasOpenRecord ? (
            <LogOut className="h-5 w-5" />
          ) : (
            <LogIn className="h-5 w-5" />
          )}
        </button>

        {/* Checkout confirmation */}
        <ConfirmActionModal
          isOpen={modalOpen}
          onClose={closeModal}
          onConfirm={handleCheckOut}
          hideIcon={true}
          title="Are you sure you want to check out?"
          isLoading={isCheckingOut}
          showComment={false}
        />
      </div>
    </>
  );
};

export default AttendanceButton;

